// pages/locations/LocationCreate.js

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useGlobal } from '../../context/GlobalContext'
import '../../assets/css/LocationCreate.css'

function LocationCreate() {
  const [sites, setSites] = useState([])
  const [siteId, setSiteId] = useState('')
  const [locationName, setLocationName] = useState('')
  const [address, setAddress] = useState('')
  const [lat, setLat] = useState(0)
  const [lng, setLng] = useState(0)
  const [mapRef, setMapRef] = useState(null)
  const [existingLocations, setExistingLocations] = useState([])
  const navigate = useNavigate()
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  })
  const { showLoading, hideLoading, showMessage } = useGlobal()

  const getUserLocation = () => new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        setLat(pos.coords.latitude)
        setLng(pos.coords.longitude)
        resolve()
      }, () => {
        showMessage('Unable to get location', 'error')
        reject()
      })
    } else {
      showMessage('Geolocation is not supported by this browser', 'error')
      reject()
    }
  })

  const fetchLocations = async newSiteId => {
    if (!newSiteId) {
      setExistingLocations([])
      return
    }
    showLoading()
    try {
      const res = await api.get(`/location/all?siteId=${newSiteId}`)
      setExistingLocations(res.data.locations || [])
    } catch {
      showMessage('Error fetching locations', 'error')
    }
    hideLoading()
  }

  useEffect(() => {
    ;(async () => {
      showLoading()
      try {
        await getUserLocation()
        const res = await api.get('/site/all')
        if (res.data && res.data.sites) {
          setSites(res.data.sites)
        }
      } catch {
        showMessage('Error loading sites or location', 'error')
      } finally {
        hideLoading()
      }
    })()
  }, [])

  useEffect(() => {
    if (mapRef) {
      mapRef.panTo({ lat, lng })
    }
  }, [mapRef, lat, lng])

  const fetchCurrentLocation = async () => {
    showLoading()
    try {
      await getUserLocation()
      if (mapRef) {
        mapRef.panTo({ lat, lng })
      }
    } finally {
      hideLoading()
    }
  }

  const onLoadMap = useCallback(map => {
    setMapRef(map)
  }, [])

  const onUnmountMap = useCallback(() => {
    setMapRef(null)
  }, [])

  const handleMarkerDragEnd = e => {
    setLat(e.latLng.lat())
    setLng(e.latLng.lng())
  }

  const geocodeAddress = async addressValue => {
    if (!window.google) return
    const geocoder = new window.google.maps.Geocoder()
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address: addressValue }, (results, status) => {
        if (status === 'OK' && results[0]) resolve(results[0].geometry.location)
        else reject()
      })
    })
  }

  const handleSiteChange = async e => {
    const newSiteId = e.target.value
    setSiteId(newSiteId)
    const selected = sites.find(l => l._id === newSiteId)
    if (selected) {
      setAddress(selected.address)
      await fetchLocations(newSiteId)
      if (selected.address && window.google) {
        showLoading()
        try {
          const location = await geocodeAddress(selected.address)
          if (location && mapRef) {
            setLat(location.lat())
            setLng(location.lng())
            mapRef.panTo({ lat: location.lat(), lng: location.lng() })
          }
        } catch {
          showMessage('Could not find address on map', 'error')
        } finally {
          hideLoading()
        }
      }
    } else {
      setAddress('')
      setExistingLocations([])
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    try {
      showLoading()
      const payload = { locationName, siteId, lat, lng, address }
      const res = await api.post('/location/create', payload)
      if (res.data && res.data.locationId) {
        showMessage('Location created successfully', 'success')
        navigate('/dashboard')
      } else {
        showMessage('Failed to create location', 'error')
      }
    } catch {
      showMessage('Error creating location', 'error')
    } finally {
      hideLoading()
    }
  }

  if (loadError) {
    return (
      <div className="location-page-container">
        <h2>Create Location</h2>
        <p>Map failed to load.</p>
      </div>
    )
  }

  return (
    <div className="location-page-container">
      <h2 className="location-title">Create Location</h2>
      <form onSubmit={handleSubmit} className="location-form">
        <div className="location-form-group">
          <label className="location-label">Select Site</label>
          <select
            className="location-select"
            value={siteId}
            onChange={handleSiteChange}
            required
          >
            <option value="">-- Choose --</option>
            {sites.map(l => (
              <option key={l._id} value={l._id}>
                {l.plainName} - {l.address}
              </option>
            ))}
          </select>
        </div>
        <div className="location-form-group">
          <label className="location-label">Location Name</label>
          <input
            className="location-input"
            value={locationName}
            onChange={e => setLocationName(e.target.value)}
            required
          />
        </div>
        
       
        <button type="button" onClick={fetchCurrentLocation} className="location-btn">
          Get Current Location
        </button>
        {isLoaded && (
          <div className="location-map-container">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={{ lat, lng }}
              zoom={19} 
              onLoad={onLoadMap}
              onUnmount={onUnmountMap}
              options={{
                mapTypeId: 'satellite',
                disableDefaultUI: false,
                zoomControl: true
              }}
            >
              {existingLocations.map(el => (
                <Marker
                  key={el._id}
                  position={{
                    lat: parseFloat(el.lat) || 0,
                    lng: parseFloat(el.lng) || 0
                  }}
                  icon="http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                />
              ))}
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            </GoogleMap>
          </div>
        )}
        <button type="submit" className="location-btn location-submit">
          Create Location
        </button>
      </form>
      {existingLocations.length > 0 && (
        <div className="existing-locations-list">
          <h3>Existing Locations for this Site:</h3>
          <ul>
            {existingLocations.map(el => (
              <li key={el._id}>
                {el.plainLocationName}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default LocationCreate